// Logger utility for Quizenie app
class Logger {
  constructor() {
    this.logLevel = this.getLogLevel();
    this.logs = [];
    this.maxLogs = 1000; // Maximum number of logs to keep in memory
    this.enableConsoleOutput = true;
    this.enableLocalStorage = true;
  }

  // Log levels
  static LEVELS = {
    ERROR: 0,
    WARN: 1,
    INFO: 2,
    DEBUG: 3
  };

  static LEVEL_NAMES = {
    0: 'ERROR',
    1: 'WARN',
    2: 'INFO',
    3: 'DEBUG'
  };

  // Get log level from environment or default to INFO
  getLogLevel() {
    const envLevel = process.env.REACT_APP_LOG_LEVEL || 'INFO';
    return Logger.LEVELS[envLevel.toUpperCase()] || Logger.LEVELS.INFO;
  }

  // Core logging method
  log(level, message, data = null, context = null) {
    if (level > this.logLevel) return;

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: Logger.LEVEL_NAMES[level],
      message,
      data,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Add to memory logs
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest log
    }

    // Console output
    if (this.enableConsoleOutput) {
      this.outputToConsole(logEntry);
    }

    // Local storage (for debugging)
    if (this.enableLocalStorage) {
      this.saveToLocalStorage(logEntry);
    }

    // Send to analytics/monitoring service if configured
    this.sendToAnalytics(logEntry);
  }

  // Output to console with appropriate styling
  outputToConsole(logEntry) {
    const { level, timestamp, message, data, context } = logEntry;
    const timeStr = new Date(timestamp).toLocaleTimeString();
    
    const styles = {
      ERROR: 'color: #ff4444; font-weight: bold;',
      WARN: 'color: #ffaa00; font-weight: bold;',
      INFO: 'color: #0088ff;',
      DEBUG: 'color: #888888;'
    };

    const prefix = `[${timeStr}] [${level}]`;
    const contextStr = context ? ` [${context}]` : '';
    
    console.log(
      `%c${prefix}${contextStr} ${message}`,
      styles[level] || '',
      data ? data : ''
    );
  }

  // Save to local storage for debugging
  saveToLocalStorage(logEntry) {
    try {
      const savedLogs = JSON.parse(localStorage.getItem('quizenie_logs') || '[]');
      savedLogs.push(logEntry);
      
      // Keep only last 500 logs in localStorage
      if (savedLogs.length > 500) {
        savedLogs.splice(0, savedLogs.length - 500);
      }
      
      localStorage.setItem('quizenie_logs', JSON.stringify(savedLogs));
    } catch (error) {
      console.warn('Failed to save log to localStorage:', error);
    }
  }

  // Send to analytics service (placeholder for future implementation)
  sendToAnalytics(logEntry) {
    // This could be connected to services like:
    // - Google Analytics
    // - Firebase Analytics
    // - Custom analytics endpoint
    // - Error tracking services like Sentry
    
    if (logEntry.level === 'ERROR') {
      // Send errors to error tracking service
      this.sendErrorToTracking(logEntry);
    }
  }

  // Error tracking (placeholder)
  sendErrorToTracking(logEntry) {
    // Example: Send to Sentry, LogRocket, etc.
    // Sentry.captureException(new Error(logEntry.message), {
    //   extra: logEntry.data,
    //   tags: { context: logEntry.context }
    // });
  }

  // Public logging methods
  error(message, data = null, context = null) {
    this.log(Logger.LEVELS.ERROR, message, data, context);
  }

  warn(message, data = null, context = null) {
    this.log(Logger.LEVELS.WARN, message, data, context);
  }

  info(message, data = null, context = null) {
    this.log(Logger.LEVELS.INFO, message, data, context);
  }

  debug(message, data = null, context = null) {
    this.log(Logger.LEVELS.DEBUG, message, data, context);
  }

  // Convenience methods for common use cases
  userAction(action, data = null) {
    this.info(`User action: ${action}`, data, 'USER_ACTION');
  }

  apiCall(endpoint, method = 'GET', data = null) {
    this.debug(`API call: ${method} ${endpoint}`, data, 'API');
  }

  apiError(endpoint, error, data = null) {
    this.error(`API error: ${endpoint}`, { error: error.message, data }, 'API');
  }

  componentMount(componentName) {
    this.debug(`Component mounted: ${componentName}`, null, 'COMPONENT');
  }

  componentUnmount(componentName) {
    this.debug(`Component unmounted: ${componentName}`, null, 'COMPONENT');
  }

  // Performance logging
  startTimer(name) {
    this.timers = this.timers || {};
    this.timers[name] = performance.now();
    this.debug(`Timer started: ${name}`, null, 'PERFORMANCE');
  }

  endTimer(name) {
    this.timers = this.timers || {};
    if (this.timers[name]) {
      const duration = performance.now() - this.timers[name];
      this.info(`Timer ended: ${name} - ${duration.toFixed(2)}ms`, { duration }, 'PERFORMANCE');
      delete this.timers[name];
      return duration;
    }
    return null;
  }

  // Get logs for debugging
  getLogs(level = null, context = null, limit = null) {
    let filteredLogs = this.logs;

    if (level) {
      const levelNum = Logger.LEVELS[level.toUpperCase()];
      filteredLogs = filteredLogs.filter(log => 
        Logger.LEVELS[log.level] <= levelNum
      );
    }

    if (context) {
      filteredLogs = filteredLogs.filter(log => log.context === context);
    }

    if (limit) {
      filteredLogs = filteredLogs.slice(-limit);
    }

    return filteredLogs;
  }

  // Get logs from localStorage
  getStoredLogs() {
    try {
      return JSON.parse(localStorage.getItem('quizenie_logs') || '[]');
    } catch {
      return [];
    }
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    localStorage.removeItem('quizenie_logs');
    this.info('Logs cleared', null, 'LOGGER');
  }

  // Export logs for debugging
  exportLogs() {
    const allLogs = [...this.getStoredLogs(), ...this.logs];
    const blob = new Blob([JSON.stringify(allLogs, null, 2)], 
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quizenie-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.info('Logs exported', null, 'LOGGER');
  }
}

// Create singleton instance
const logger = new Logger();

// Initialize logger
logger.info('Logger initialized', { 
  logLevel: logger.logLevel,
  maxLogs: logger.maxLogs 
}, 'LOGGER');

export default logger;